import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ACTIVE_HIPAA_VERSION, getHipaaBody } from '../legal/hipaa';
import { ATTESTATION_SELF } from '../legal/hipaa/v3';
import { renderTemplate } from '../utils/renderTemplate';
import { generateHipaaPdf } from '../utils/generateHipaaPdf';
import SignaturePad from '../components/SignaturePad';
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';
import '@styles/pages/hipaa.css';
import '@styles/pages/family.css';

type TokenInfo = {
  valid: boolean;
  error?: string;
  family_member?: {
    id: string;
    first_name: string;
    last_name: string;
    date_of_birth: string;
    relationship: string;
    email: string;
  };
  parent_name?: string;
  user_id?: string;
  expires_at?: string;
};

export default function HipaaSignPublic() {
  const { token } = useParams<{ token: string }>();

  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [attestation, setAttestation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const today = new Date().toLocaleDateString('en-US');

  /* ---------------------------
     VALIDATE TOKEN
  ---------------------------- */
  useEffect(() => {
    if (!token) return;

    const validate = async () => {
      const { data, error: rpcError } = await supabase.rpc(
        'get_signing_token_info',
        { p_token: token }
      );

      if (rpcError) {
        setTokenInfo({ valid: false, error: 'Unable to validate signing link' });
      } else {
        setTokenInfo(data as TokenInfo);
      }
      setLoading(false);
    };

    validate();
  }, [token]);

  /* ---------------------------
     LOADING STATE
  ---------------------------- */
  if (loading) {
    return (
      <div className="hipaa-container">
        <header className="auth-header">
          <div className="logo" aria-label="Mediloop">
            <img src="/images/logos/tail-logo.png" alt="Mediloop" className="logo-img" width={36} height={36} />
          </div>
        </header>
        <main className="hipaa-card" style={{ textAlign: 'center', padding: '60px 24px' }}>
          <p style={{ color: 'var(--color-text-muted)' }}>Validating signing link…</p>
        </main>
      </div>
    );
  }

  /* ---------------------------
     INVALID / EXPIRED / USED TOKEN
  ---------------------------- */
  if (!tokenInfo?.valid) {
    return (
      <div className="hipaa-container">
        <header className="auth-header">
          <div className="logo" aria-label="Mediloop">
            <img src="/images/logos/tail-logo.png" alt="Mediloop" className="logo-img" width={36} height={36} />
          </div>
        </header>
        <main className="hipaa-card" style={{ textAlign: 'center', padding: '60px 24px' }}>
          <AlertCircle size={40} style={{ color: 'rgb(239, 68, 68)', marginBottom: 12 }} />
          <h2 style={{ marginBottom: 8 }}>Link unavailable</h2>
          <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
            {tokenInfo?.error || 'This signing link is invalid, expired, or has already been used.'}
          </p>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 12 }}>
            Please ask your spouse to send a new signing link from their Mediloop account.
          </p>
        </main>
      </div>
    );
  }

  /* ---------------------------
     SUCCESS STATE
  ---------------------------- */
  if (success) {
    return (
      <div className="hipaa-container">
        <header className="auth-header">
          <div className="logo" aria-label="Mediloop">
            <img src="/images/logos/tail-logo.png" alt="Mediloop" className="logo-img" width={36} height={36} />
          </div>
        </header>
        <main className="hipaa-card" style={{ textAlign: 'center', padding: '60px 24px' }}>
          <CheckCircle size={48} style={{ color: 'rgb(34, 197, 94)', marginBottom: 12 }} />
          <h2 style={{ marginBottom: 8 }}>Authorization signed!</h2>
          <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
            Thank you, {tokenInfo.family_member?.first_name}. Your HIPAA
            authorization has been recorded. You can close this page.
          </p>
          <div className="trust-row" style={{ marginTop: 20 }}>
            <svg
              width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>Your information is encrypted and HIPAA-protected</span>
          </div>
        </main>
      </div>
    );
  }

  /* ---------------------------
     SIGNING FORM
  ---------------------------- */
  const fm = tokenInfo.family_member!;
  const spouseName = `${fm.first_name} ${fm.last_name}`;
  const accountHolder = tokenInfo.parent_name || 'your spouse';

  const consentText = renderTemplate(getHipaaBody('spouse'), {
    full_name: spouseName,
    date_of_birth: fm.date_of_birth,
    printed_name: spouseName,
    account_holder_name: accountHolder,
    signature: '[Signed electronically]',
    today,
  });

  const handleSubmit = async () => {
    if (!accepted) {
      setError('You must accept the HIPAA Release Authorization');
      return;
    }
    if (!attestation) {
      setError('You must confirm the legal attestation');
      return;
    }
    if (!signatureData) {
      setError('Please draw your signature above');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Generate PDF
      const pdfBlob = generateHipaaPdf({
        consentText,
        signatureDataUrl: signatureData,
        fullName: spouseName,
        dateOfBirth: fm.date_of_birth,
        dateSigned: today,
        consentVersion: ACTIVE_HIPAA_VERSION,
        signerRelationship: 'spouse',
        attestationChecked: true,
      });

      // Upload PDF to storage (anon has insert on hipaa-consents bucket)
      const fileName = `${tokenInfo.user_id}/${ACTIVE_HIPAA_VERSION}-spouse-${fm.id}-${Date.now()}.pdf`;

      const { error: uploadError } = await supabase.storage
        .from('hipaa-consents')
        .upload(fileName, pdfBlob, { contentType: 'application/pdf' });

      if (uploadError) {
        setError(`Upload failed: ${uploadError.message}`);
        setSubmitting(false);
        return;
      }

      // Submit consent via RPC (SECURITY DEFINER)
      const { data: result, error: rpcError } = await supabase.rpc(
        'submit_spouse_hipaa_consent',
        {
          p_token: token!,
          p_consent_version: ACTIVE_HIPAA_VERSION,
          p_consent_text: consentText,
          p_signed_name: spouseName,
          p_signed_pdf_path: fileName,
          p_user_agent: navigator.userAgent,
        }
      );

      if (rpcError) {
        setError(rpcError.message);
        setSubmitting(false);
        return;
      }

      const rpcResult = result as { success: boolean; error?: string };

      if (!rpcResult.success) {
        setError(rpcResult.error || 'Failed to save authorization');
        setSubmitting(false);
        return;
      }

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = accepted && attestation && !!signatureData;

  // Show expiration warning
  const expiresAt = tokenInfo.expires_at ? new Date(tokenInfo.expires_at) : null;
  const hoursLeft = expiresAt
    ? Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60)))
    : null;

  return (
    <div className="hipaa-container">
      <header className="auth-header">
        <div className="logo" aria-label="Mediloop">
          <img src="/images/logos/tail-logo.png" alt="Mediloop" className="logo-img" width={36} height={36} />
        </div>
      </header>

      <main className="hipaa-card">
        <div className="hipaa-eyebrow">  Secure signing request  </div>
        <h1>HIPAA Release <span className="italic">Authorization.</span></h1>
        <p className="subtitle">
          Hi {fm.first_name}, {accountHolder} has requested your HIPAA
          authorization through Mediloop so they can submit your medical bills
          for negotiation.
        </p>

        {hoursLeft !== null && hoursLeft <= 6 && (
          <div className="spouse-signing-note" style={{ marginBottom: 16 }}>
            <Clock size={14} />
            <span>This link expires in {hoursLeft} hour{hoursLeft !== 1 ? 's' : ''}</span>
          </div>
        )}

        <div className="hipaa-content">
          <pre className="hipaa-box">{consentText}</pre>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span>
              I have read and agree to the HIPAA Release Authorization
            </span>
          </label>

          <label className="checkbox attestation-checkbox">
            <input
              type="checkbox"
              checked={attestation}
              onChange={(e) => setAttestation(e.target.checked)}
            />
            <span className="attestation-text">
              <strong>Legal Attestation:</strong> {ATTESTATION_SELF}
            </span>
          </label>

          <SignaturePad onSignatureChange={setSignatureData} />

          {error && <p className="form-error">{error}</p>}

          <button
            className="primary"
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
          >
            {submitting ? 'Saving…' : 'Sign & Complete'}
          </button>
        </div>

        <div className="trust-row">
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>Your information is encrypted and HIPAA-protected</span>
        </div>
      </main>
    </div>
  );
}
